"use client";

import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ThemeToggle from "../components/ui/themeToggle";
import { IconLogout, IconRefresh, IconSearch } from "@tabler/icons-react";
import { getApprovedToken, isApprovedAuthenticated, removeApprovedToken } from "../utils/approvedAuth";

type UserItem = {
  user_id: string;
  username: string;
  email: string;
  status: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  is_deleted: boolean;
};

type FiltersState = {
  search: string;
  status: string;
  includeDeleted: boolean;
};

type LoadFeedback = {
  title: string;
  message: string;
  tone: "warning" | "error";
  actionHint?: string;
};

const INITIAL_FILTERS: FiltersState = {
  search: "",
  status: "",
  includeDeleted: true,
};

export default function ApprovedAdminPanel() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [filters, setFilters] = useState<FiltersState>(INITIAL_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [loadFeedback, setLoadFeedback] = useState<LoadFeedback | null>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!isApprovedAuthenticated()) {
      return;
    }

    if (hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;
    fetchUsers();
  }, []);

  if (!isApprovedAuthenticated()) {
    return <Navigate to="/aprovedlogin" replace />;
  }

  const fetchUsers = async (nextFilters: FiltersState = filters) => {
    setLoading(true);
    setLoadFeedback(null);

    try {
      const params = new URLSearchParams();

      if (nextFilters.search.trim()) {
        params.set("q", nextFilters.search.trim());
      }

      if (nextFilters.status !== "") {
        params.set("status", nextFilters.status);
      }

      params.set("include_deleted", String(nextFilters.includeDeleted));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/approved/users?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${getApprovedToken()}`,
          },
        }
      );

      if (response.status === 401 || response.status === 403) {
        removeApprovedToken();
        await Swal.fire({
          icon: "warning",
          title: "Sessão encerrada",
          text: "Seu acesso administrativo expirou. Faça login novamente.",
        });
        navigate("/aprovedlogin", { replace: true });
        return;
      }

      if (!response.ok) {
        const responseText = (await response.text()).trim();
        let errorMessage = responseText;
        try { errorMessage = JSON.parse(responseText).message || responseText; } catch { /* Plain text API error. */ }

        if (response.status === 503) {
          setUsers([]);
          setLoadFeedback({
            title: "Keycloak indisponivel",
            message:
              "O cadastro e a consulta de usuarios dependem do Keycloak. Verifique a configuracao e a disponibilidade do servico.",
            tone: "warning",
            actionHint:
              errorMessage || "Verifique a configuracao do backend e clique em atualizar.",
          });
          return;
        }

        throw new Error(errorMessage || "Não foi possível carregar os usuários.");
      }

      const data = await response.json();
      setUsers(Array.isArray(data?.users) ? data.users : []);
      setLoadFeedback(null);
    } catch (error) {
      setUsers([]);
      setLoadFeedback({
        title: "Falha ao carregar usuarios",
        message:
          error instanceof Error && error.message
            ? error.message
            : "Nao foi possivel buscar os usuarios do painel administrativo.",
        tone: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (
    field: keyof FiltersState,
    value: string | boolean
  ) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleApplyFilters = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await fetchUsers(filters);
  };

  const handleClearFilters = async () => {
    setFilters(INITIAL_FILTERS);
    await fetchUsers(INITIAL_FILTERS);
  };

  const updateUserStatus = async (user: UserItem, status: number) => {
    const actionLabel = status === 1 ? "aprovar" : "bloquear";

    if (user.status === status) {
      await Swal.fire({
        icon: "info",
        title: status === 1 ? "Usuário já ativo" : "Usuário já bloqueado",
        text:
          status === 1
            ? "Esse cadastro já está aprovado, então nenhuma alteração foi necessária."
            : "Esse cadastro já está pendente ou bloqueado, então nenhuma alteração foi necessária.",
      });
      return;
    }

    setActionLoadingId(user.user_id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/approved/users/${encodeURIComponent(user.user_id)}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getApprovedToken()}`,
          },
          body: JSON.stringify({ status }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data?.message === "string"
            ? data.message
            : `Não foi possível ${actionLabel} o usuário.`
        );
      }

      await Swal.fire({
        icon: typeof data?.warning === "string" ? "warning" : "success",
        title: "Status atualizado",
        text:
          typeof data?.warning === "string" ? data.warning :
          status === 1
            ? "O usuário foi aprovado com sucesso."
            : "O usuário foi bloqueado com sucesso.",
      });

      await fetchUsers(filters);
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Falha na atualização",
        text:
          error instanceof Error
            ? error.message
            : `Não foi possível ${actionLabel} o usuário.`,
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (user: UserItem) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Excluir usuário?",
      text: `O cadastro de ${user.username} será removido apenas de forma lógica e poderá ser restaurado depois.`,
      showCancelButton: true,
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionLoadingId(user.user_id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/approved/users/${encodeURIComponent(user.user_id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getApprovedToken()}`,
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data?.message === "string"
            ? data.message
            : "Não foi possível excluir o usuário."
        );
      }

      await Swal.fire({
        icon: "success",
        title: "Usuário excluído",
        text: "O cadastro está desativado e pode ser encontrado em Desativados para restauração.",
      });

      if (!filters.includeDeleted) {
        setUsers((current) => current.filter((item) => item.user_id !== user.user_id));
      } else {
        await fetchUsers(filters);
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Erro ao excluir",
        text:
          error instanceof Error
            ? error.message
            : "Não foi possível excluir o usuário.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRestore = async (user: UserItem) => {
    setActionLoadingId(user.user_id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/approved/users/${encodeURIComponent(user.user_id)}/restore`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${getApprovedToken()}`,
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data?.message === "string"
            ? data.message
            : "Não foi possível restaurar o usuário."
        );
      }

      await Swal.fire({
        icon: typeof data?.warning === "string" ? "warning" : "success",
        title: "Usuário restaurado",
        text: typeof data?.warning === "string" ? data.warning : "O cadastro foi restaurado com o estado de aprovação anterior. Contas pendentes ainda precisam de aprovação.",
      });

      await fetchUsers(filters);
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Erro ao restaurar",
        text:
          error instanceof Error
            ? error.message
            : "Não foi possível restaurar o usuário.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleEdit = async (user: UserItem) => {
    const result = await Swal.fire({
      title: "Editar usuário",
      html: `
        <div class="flex flex-col gap-3 text-left">
          <label class="flex flex-col gap-2 text-sm font-medium text-slate-700 dark:text-zinc-200">
            <span>Nome</span>
            <input id="swal-username" class="w-full rounded-xl border border-slate-300 dark:border-zinc-600 px-4 py-3 text-base text-slate-900 dark:text-zinc-100 outline-none transition focus:border-slate-500" placeholder="Nome" value="${escapeHtml(
              user.username
            )}" />
          </label>
          <label class="flex flex-col gap-2 text-sm font-medium text-slate-700 dark:text-zinc-200">
            <span>E-mail</span>
            <input id="swal-email" class="w-full rounded-xl border border-slate-300 dark:border-zinc-600 px-4 py-3 text-base text-slate-900 dark:text-zinc-100 outline-none transition focus:border-slate-500" placeholder="E-mail" value="${escapeHtml(
              user.email
            )}" />
          </label>
          <label class="flex flex-col gap-2 text-sm font-medium text-slate-700 dark:text-zinc-200">
            <span>Status</span>
            <select id="swal-status" class="w-full rounded-xl border border-slate-300 dark:border-zinc-600 px-4 py-3 text-base text-slate-900 dark:text-zinc-100 outline-none transition focus:border-slate-500">
              <option value="0" ${user.status === 0 ? "selected" : ""}>Pendente</option>
              <option value="1" ${user.status === 1 ? "selected" : ""}>Aprovado</option>
            </select>
          </label>
        </div>
      `,
      width: "min(92vw, 34rem)",
      padding: "1.25rem",
      customClass: {
        popup: "rounded-[24px] sm:rounded-[28px]",
        title: "text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-zinc-100",
        htmlContainer: "mx-0 mt-5 px-0 pb-0 overflow-hidden",
        actions: "mt-6 flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-end",
        confirmButton:
          "m-0 inline-flex w-full items-center justify-center rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-600 sm:w-auto",
        cancelButton:
          "m-0 inline-flex w-full items-center justify-center rounded-xl border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-5 py-3 text-sm font-semibold text-slate-700 dark:text-zinc-200 transition hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-800 sm:w-auto",
      },
      buttonsStyling: false,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const username = (document.getElementById("swal-username") as HTMLInputElement)?.value?.trim();
        const email = (document.getElementById("swal-email") as HTMLInputElement)?.value?.trim();
        const status = Number(
          (document.getElementById("swal-status") as HTMLSelectElement)?.value ?? "0"
        );

        if (!username || !email) {
          Swal.showValidationMessage("Preencha nome e e-mail.");
          return;
        }

        return { username, email, status };
      },
    });

    if (!result.isConfirmed || !result.value) {
      return;
    }

    setActionLoadingId(user.user_id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/approved/users/${encodeURIComponent(user.user_id)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getApprovedToken()}`,
          },
          body: JSON.stringify(result.value),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data?.message === "string"
            ? data.message
            : "Não foi possível salvar as alterações."
        );
      }

      await Swal.fire({
        icon: typeof data?.warning === "string" ? "warning" : "success",
        title: "Usuário atualizado",
        text: typeof data?.warning === "string" ? data.warning : "Os dados do cadastro foram atualizados com sucesso.",
      });

      await fetchUsers(filters);
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Erro ao editar",
        text:
          error instanceof Error
            ? error.message
            : "Não foi possível salvar as alterações.",
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      icon: "question",
      title: "Sair da área administrativa?",
      text: "Você precisará informar novamente as credenciais do administrador para voltar ao painel.",
      showCancelButton: true,
      confirmButtonText: "Sair",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) {
      return;
    }

    removeApprovedToken();

    await Swal.fire({
      icon: "success",
      title: "Sessão encerrada",
      text: "Você saiu da área administrativa com segurança.",
    });

    navigate("/aprovedlogin", { replace: true });
  };

  return (
    <div className="admin-page min-h-screen bg-slate-100 dark:bg-zinc-950 px-4 pb-6 pt-20 text-slate-900 dark:text-zinc-100 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl rounded-[28px] bg-white dark:bg-zinc-900 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.12)] ring-1 ring-slate-200 dark:ring-zinc-700 sm:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-sm font-medium tracking-wide text-slate-500 dark:text-zinc-400">
              Painel administrativo
            </p>
            <h1 className="mt-2 max-w-xl text-3xl font-semibold leading-tight text-slate-950 dark:text-zinc-100 sm:text-4xl">
              Aprovação de usuários
            </h1>
            <p className="mt-3 max-w-xl text-base leading-7 text-slate-600 dark:text-zinc-300 sm:text-lg">
              Visualize os cadastros, filtre por status, edite os
              dados e aprove, bloqueie, exclua ou restaure usuários conforme a
              necessidade.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <ThemeToggle inline />
            <button
              type="button"
              onClick={() => fetchUsers(filters)}
              aria-label="Atualizar usuários"
              title="Atualizar usuários"
              className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-slate-700 dark:text-zinc-200 transition hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-800"
            >
              <IconRefresh size={20} />
            </button>
            <button
              type="button"
              onClick={() => setShowFilters((current) => !current)}
              aria-label={showFilters ? "Ocultar filtros" : "Localizar usuários"}
              title={showFilters ? "Ocultar filtros" : "Localizar usuários"}
              className={`flex h-12 w-12 items-center justify-center rounded-xl border transition ${
                showFilters
                  ? "border-sky-600 bg-sky-50 text-sky-700"
                  : "border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-slate-700 dark:text-zinc-200 hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-800"
              }`}
            >
              <IconSearch size={20} />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sair da área administrativa"
              title="Sair da área administrativa"
              className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-white transition hover:bg-slate-800"
            >
              <IconLogout size={20} />
            </button>
          </div>
        </div>

        {showFilters ? (
          <form
            onSubmit={handleApplyFilters}
            className="mt-8 rounded-2xl border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800 p-4 sm:p-5"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">Filtros de busca</h2>
                <p className="text-sm text-slate-500 dark:text-zinc-400">
                  Use os campos abaixo para localizar usuários com mais rapidez.
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <label className="flex flex-col gap-2 text-sm font-medium text-slate-700 dark:text-zinc-200">
                Busca por nome ou e-mail
                <input
                  type="text"
                  value={filters.search}
                  onChange={(event) => handleFilterChange("search", event.target.value)}
                  placeholder="Digite um nome ou e-mail"
                  className="h-12 rounded-xl border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-4 text-sm text-slate-900 dark:text-zinc-100 outline-none transition focus:border-slate-500"
                />
              </label>



              <label className="flex flex-col gap-2 text-sm font-medium text-slate-700 dark:text-zinc-200">
                Status
                <select
                  value={filters.status}
                  onChange={(event) => handleFilterChange("status", event.target.value)}
                  className="h-12 rounded-xl border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-4 text-sm text-slate-900 dark:text-zinc-100 outline-none transition focus:border-slate-500"
                >
                  <option value="">Todos</option>
                  <option value="0">Desativados (pendentes, bloqueados e excluídos)</option>
                  <option value="1">Aprovados</option>
                </select>
              </label>

              <label className="flex flex-col gap-2 text-sm font-medium text-slate-700 dark:text-zinc-200">
                Incluir excluídos
                <span className="flex h-12 items-center gap-3 rounded-xl border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-4 text-sm text-slate-700 dark:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={filters.includeDeleted}
                    onChange={(event) => handleFilterChange("includeDeleted", event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 dark:border-zinc-600"
                  />
                  <span>Exibir usuários excluídos</span>
                </span>
              </label>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="submit"
                className="rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-600"
              >
                Aplicar filtros
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="rounded-xl border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-5 py-3 text-sm font-semibold text-slate-700 dark:text-zinc-200 transition hover:border-slate-400 hover:bg-slate-100 dark:hover:bg-zinc-800"
              >
                Limpar filtros
              </button>
            </div>
          </form>
        ) : null}

        {loading ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 dark:border-zinc-600 bg-slate-50 dark:bg-zinc-800 px-6 py-12 text-center text-sm text-slate-500 dark:text-zinc-400">
            Carregando usuários do painel...
          </div>
        ) : loadFeedback ? (
          <div
            className={`mt-8 rounded-2xl px-6 py-12 text-center ${
              loadFeedback.tone === "warning"
                ? "border border-amber-200 bg-amber-50"
                : "border border-rose-200 bg-rose-50"
            }`}
          >
            <h2
              className={`text-base font-semibold ${
                loadFeedback.tone === "warning" ? "text-amber-900" : "text-rose-900"
              }`}
            >
              {loadFeedback.title}
            </h2>
            <p
              className={`mt-2 text-sm leading-6 ${
                loadFeedback.tone === "warning" ? "text-amber-800" : "text-rose-800"
              }`}
            >
              {loadFeedback.message}
            </p>
            {loadFeedback.actionHint ? (
              <p
                className={`mt-3 text-sm ${
                  loadFeedback.tone === "warning" ? "text-amber-700" : "text-rose-700"
                }`}
              >
                {loadFeedback.actionHint}
              </p>
            ) : null}
          </div>
        ) : users.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 dark:border-zinc-600 bg-slate-50 dark:bg-zinc-800 px-6 py-12 text-center text-sm text-slate-500 dark:text-zinc-400">
            Nenhum usuário encontrado com os filtros atuais.
          </div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {users.map((user) => {
              const isBusy = actionLoadingId === user.user_id;

              return (
                <article
                  key={user.user_id}
                  className={`rounded-[24px] border p-5 shadow-sm transition ${
                    user.is_deleted
                      ? "border-rose-200 bg-rose-50/70 dark:border-rose-900 dark:bg-rose-950/40"
                      : "border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
                        <span>ID</span>
                        <span title={user.user_id}>{user.user_id.slice(0, 8)}</span>
                      </div>
                      <div className="min-w-0">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            user.is_deleted
                              ? "bg-rose-100 text-rose-700"
                              : user.status === 1
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {user.is_deleted
                            ? "Desativado · Excluído"
                            : user.status === 1
                            ? "Aprovado"
                            : "Desativado"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3">
                    <div>
                      <h2 className="text-2xl font-semibold leading-tight text-slate-950 dark:text-zinc-100 break-words">
                        {user.username}
                      </h2>
                      <p className="mt-2 break-all text-sm leading-6 text-slate-600 dark:text-zinc-300">
                        {user.email}
                      </p>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <InfoBlock
                          label="Criado em"
                          value={formatDateTime(user.created_at)}
                      />
                      <InfoBlock
                          label="Atualizado em"
                          value={formatDateTime(user.updated_at)}
                      />
                      {user.is_deleted && user.deleted_at ? (
                        <InfoBlock
                          label="Excluído em"
                          value={formatDateTime(user.deleted_at)}
                        />
                      ) : (
                        <InfoBlock
                          label="Status"
                          value={user.status === 1 ? "Liberado" : "Desativado / aguardando aprovação"}
                        />
                      )}
                    </div>
                  </div>

                  <div className="mt-6 grid gap-3">
                    {!user.is_deleted ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleEdit(user)}
                          disabled={isBusy}
                          className="rounded-xl border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-4 py-3 text-sm font-semibold text-slate-700 dark:text-zinc-200 transition hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isBusy ? "Processando..." : "Editar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateUserStatus(user, 1)}
                          disabled={isBusy}
                          className="rounded-xl bg-emerald-400 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isBusy ? "Processando..." : "Aprovar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateUserStatus(user, 0)}
                          disabled={isBusy}
                          className="rounded-xl bg-slate-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isBusy ? "Processando..." : "Bloquear"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(user)}
                          disabled={isBusy}
                          className="rounded-xl bg-rose-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isBusy ? "Processando..." : "Excluir"}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRestore(user)}
                        disabled={isBusy}
                          className="rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isBusy ? "Processando..." : "Restaurar usuário"}
                        </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-[0.28em] text-slate-400 dark:text-zinc-400">
        {label}
      </p>
      <p className="mt-2 whitespace-pre-line break-words text-base leading-7 text-slate-800 dark:text-zinc-200">
        {value}
      </p>
    </div>
  );
}

function formatDateTime(value: string) {
  if (!value) {
    return "-";
  }

  return value.replace(" ", "\n");
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
